import { ChangeProjectionService, Roi, RoiChange, CounterDeltas } from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface RevertChangePayload {
  /** Current roi versions whose changes should be reverted. */
  rois: Roi[];
}

export interface RevertChangesResult {
  revertedChanges: Map<string, RoiChange | null>;
  revertedRois: Map<string, Roi>;
  roisToDeleteIds: string[];
  totalReverted: number;
  counterDeltas: CounterDeltas;
}

/**
 * Reverts the changes that were applied to the rois.
 *
 * @remarks
 * - If the changedRois parameter is provided, the changes will be reverted based on the provided changes.
 * - If the changedRois parameter is not provided, all changes will be reverted (if any).
 */
export class RevertChangeUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  /**
   * @returns New label change summaries for the reverted rois
   */
  public async execute(input: Readonly<RevertChangePayload>): Promise<RevertChangesResult> {
    const { rois } = input;

    const roiIds = rois.map((roi) => roi.id);

    const previousChanges = await this.roiChanges.getByIds(roiIds);

    const partialUuids = rois
      .filter((r) => {
        if (r.additionalLabelsIncluded || !r.uuid) return false;
        const prior = previousChanges.get(r.id);
        if (prior?.isNew) return false;
        return true;
      })
      .map((r) => r.uuid as string);
    const baselines =
      partialUuids.length > 0 ? await this.rois.batchGetByIds(partialUuids) : new Map<string, Roi>();

    const hydratedRois = rois.map((roi) => {
      if (roi.additionalLabelsIncluded || !roi.uuid) return roi;
      const prior = previousChanges.get(roi.id);
      if (prior?.isNew) return prior.snapshot ?? roi;
      const baseline = baselines.get(roi.uuid);
      if (!baseline) return roi;
      return prior ? ChangeProjectionService.applyChanges(baseline, prior) : baseline;
    });

    const revertedChanges = new Map<string, RoiChange | null>();
    const revertedRois = new Map<string, Roi>();
    const roisToDeleteIds: string[] = [];
    const perRoiDeltas: CounterDeltas[] = [];

    for (const roi of hydratedRois) {
      const previousChange = previousChanges.get(roi.id);

      // TODO: verify the reverted change is present in previous changes
      if (!previousChange) {
        // No changes to revert for this ROI
        continue;
      }

      const newRoi = previousChange.isNew === true;

      const reversionChanges = newRoi
        ? { labelsAdded: [] as string[], labelsRemoved: Array.from(roi.labels.keys()) }
        : {
            labelsAdded: previousChange.labelsRemoved,
            labelsRemoved: previousChange.labelsAdded,
          };

      const revertedRoi = ChangeProjectionService.applyChanges(roi, reversionChanges);

      revertedRois.set(roi.id, revertedRoi);

      const previousLabels = Array.from(roi.labels.keys());

      if (newRoi) {
        // The ROI is leaving the universe — emit a deletion delta, not a
        // transition-to-unlabeled one. Otherwise the unlabeled counter
        // accumulates a phantom +1 per reverted new ROI.
        perRoiDeltas.push(ChangeProjectionService.computeRoiDeletionDelta(previousLabels));
        roisToDeleteIds.push(roi.id);
      } else {
        const currentLabels = Array.from(revertedRoi.labels.keys());
        const labelsAdded = currentLabels.filter((l) => !roi.labels.has(l));
        const labelsRemoved = previousLabels.filter((l) => !revertedRoi.labels.has(l));
        perRoiDeltas.push(
          ChangeProjectionService.computeCounterDeltas(previousLabels, currentLabels, labelsAdded, labelsRemoved)
        );
      }

      revertedChanges.set(roi.id, null);
    }

    if (revertedChanges.size > 0) {
      await this.roiChanges.upsertRoiChanges(revertedChanges);
    }

    const counterDeltas = ChangeProjectionService.mergeCounterDeltas(perRoiDeltas);

    return {
      revertedChanges,
      revertedRois,
      roisToDeleteIds,
      totalReverted: revertedChanges.size,
      counterDeltas,
    };
  }
}
